import { Button, Menu, Slide } from '@material-ui/core'
import React from 'react'
import { useSelector } from 'react-redux'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import CustomPeriodButton from './custom-period-modal'

export default function ChartPeriod({
    items = [],
    chartData = [],
    today = {},
    keyPrefix = 'campaign_',
    titleField = 'title',
    onChange,
    title = 'Vendas por Período',
    presets = ['7d', '1m', '3m', '6m', 'all'],
    subtitle = null,
    legend = true
}) {
    const { theme, colorsLight, colorsDark } = useSelector(state => state.appReducer)

    const [period, setPeriod] = React.useState('7d')
    const [totalAmount, setTotal] = React.useState(0)
    const [colors, setColors] = React.useState(localStorage.getItem('data-bs-theme') === 'dark' ? colorsDark : colorsLight)

    const [anchorEl, setAnchorEl] = React.useState(null)
    const [othersData, setOthersData] = React.useState([])

    const openOthers = Boolean(anchorEl)

    const [ready, setReady] = React.useState(false)

    React.useEffect(() => {

        const t = setTimeout(() => setReady(true), 1000)
        return () => clearTimeout(t)
    }, [])

    React.useEffect(() => {
        if(theme === 'dark') {
            setColors(colorsDark)
        }else{
            setColors(colorsLight)
        }
    },[theme])

    React.useEffect(() => {
        if(period !== 'personalized') {
            onChange?.({
                start_date : '',
                end_date: ''
            })
        }
        onChange?.({ period })
    }, [period, onChange])

    React.useEffect(() => {
        const totalGeral = chartData
            .flatMap(row =>
                Object.entries(row)
                    .filter(([key]) => key.startsWith(keyPrefix))
                    .map(([, value]) => Number(value || 0))
            )
            .reduce((a, b) => a + b, 0)

        setTotal(totalGeral)
    }, [chartData, keyPrefix])

    const formatNumber = (value) => {
        if (value >= 1000000) {
            const num = value / 1000000
            return (Number.isInteger(num) ? num : num.toFixed(1).replace('.', ',')) + 'M'
        }

        if (value >= 1000) {
            const num = value / 1000
            return (Number.isInteger(num) ? num : num.toFixed(1).replace('.', ',')) + 'k'
        }

        return value
    }

    const formatMoney = (value) => {
        return Number(value || 0).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        })
    }

    const formatPercent = (value) => {
        const abs = Math.abs(Number(value || 0))

        return `${abs.toLocaleString('pt-BR', {
            minimumFractionDigits: abs % 1 !== 0 ? 1 : 0,
            maximumFractionDigits: 1
        })}%`
    }

    const dataBar = React.useMemo(() => {
        if (!items?.length || !chartData?.length) return []

        return items
            .map(item => {
                const key = `${keyPrefix}${item.id}`

                const total = chartData.reduce((sum, row) => {
                    return sum + Number(row[key] || 0)
                }, 0)

                return {
                    id: item.id,
                    key,
                    title: item[titleField],
                    total
                }
            })
            .sort((a, b) => {
                const aHasValue = Number(a.total) > 0
                const bHasValue = Number(b.total) > 0

                if (aHasValue && !bHasValue) return -1
                if (!aHasValue && bHasValue) return 1

                return Number(b.total) - Number(a.total)
            })
    }, [items, chartData, keyPrefix, titleField])

    const legendData = React.useMemo(() => {
        return dataBar.map((item, i) => ({
            ...item,
            color: colors[i % colors.length]
        }))
    }, [dataBar, colors])

    const legendDisplayData = React.useMemo(() => {
        const maxItems = 6;

        if (!legendData?.length) return [];

        if (legendData.length <= maxItems) {
            return legendData;
        }

        const topItems = legendData.slice(0, maxItems - 1);
        const others = legendData.slice(maxItems - 1);

        const othersTotal = others.reduce((sum, item) => sum + Number(item.total || 0), 0);

        return [
            ...topItems,
            {
                key: 'others',
                title: `+ ${others.length} outros`,
                total: othersTotal,
                color: '#64748b',
                isOthers: true,
                items: others
            }
        ];
    }, [legendData])

    const metaMap = React.useMemo(() => {
        return legendData.reduce((acc, item) => {
            acc[item.key] = item
            return acc
        }, {})
    }, [legendData])

    const CustomTooltip = ({ active, payload, label }) => {
        if (!active || !payload || !payload.length) return null

        const itemsTooltip = payload
            .map(entry => {
                const meta = metaMap[entry.dataKey]

                return {
                    key: entry.dataKey,
                    name: meta?.title || entry.name,
                    color: meta?.color || entry.color,
                    value: Number(entry.value || 0)
                }
            })
            .filter(item => item.value > 0)
            .sort((a, b) => b.value - a.value)

        if (!itemsTooltip.length) return null

        const total = itemsTooltip.reduce((sum, item) => sum + item.value, 0)

        return (
            <div
                style={{
                    backgroundColor: 'var(--bs-chart-card-bg)',
                    border: '1px solid var(--bs-chart-card-border)',
                    borderRadius: 12,
                    padding: '12px 14px',
                    zIndex: 999999,
                    width: 320,
                    maxWidth: '82vw',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.25)'
                }}
            >
                <div
                    style={{
                        color: 'var(--bs-chart-card-title)',
                        fontSize: 14,
                        marginBottom: 10,
                        fontWeight: 600,
                        lineHeight: 1.35,
                        wordBreak: 'break-word'
                    }}
                >
                    {payload?.[0]?.payload?.label_full || label}
                </div>

                {itemsTooltip.map(item => (
                    <div
                        key={item.key}
                        style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            gap: 10,
                            marginBottom: 10
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                flex: 1,
                                minWidth: 0,
                                marginRight: 8
                            }}
                        >
                            <span
                                style={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: 3,
                                    backgroundColor: item.color,
                                    display: 'inline-block',
                                    marginRight: 8,
                                    marginTop: 4,
                                    flexShrink: 0
                                }}
                            />

                            <span
                                style={{
                                    color: 'var(--bs-chart-card-color)',
                                    fontWeight: 600,
                                    fontSize: 14,
                                    lineHeight: 1.3,
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'anywhere',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden'
                                }}
                            >
                                {item.name}
                            </span>
                        </div>

                        <span
                            style={{
                                color: 'var(--bs-chart-card-color)',
                                fontWeight: 700,
                                fontSize: 14,
                                whiteSpace: 'nowrap',
                                flexShrink: 0
                            }}
                        >
                            {formatMoney(item.value)}
                        </span>
                    </div>
                ))}

                <div
                    style={{
                        height: 1,
                        backgroundColor: 'var(--bs-chart-divider)',
                        margin: '15px 0'
                    }}
                />

                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 10
                    }}
                >
                    <span
                        style={{
                            color: 'var(--bs-chart-card-color)',
                            fontWeight: 700,
                            fontSize: 14
                        }}
                    >
                        Total
                    </span>

                    <span
                        style={{
                            color: 'var(--bs-chart-card-color)',
                            fontWeight: 800,
                            fontSize: 15,
                            whiteSpace: 'nowrap',
                            flexShrink: 0
                        }}
                    >
                        {formatMoney(total)}
                    </span>
                </div>
            </div>
        )
    }

    const Transition = React.forwardRef((props, ref) => {
        return <Slide direction="up" ref={ref} {...props} />
    })

    return (
        <>
            {(Boolean(openOthers)) &&
            <Menu
                anchorEl={anchorEl}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                TransitionComponent={Transition}
                open={openOthers}
                onClose={() => setAnchorEl(null)}
                className="menu"
            >
                <div className="card-body pb-4 bs-bg">
                    <label className="fw-bold label-custom bs-color-b">Outros</label>

                    {othersData.map(item => (
                        <div
                            key={item.key}
                            className="d-flex align-items-center mb-2"
                        >
                            <span
                                style={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: 3,
                                    backgroundColor: item.color,
                                    display: 'inline-block',
                                    marginRight: 10,
                                    flexShrink: 0
                                }}
                            />

                            <small
                                className="bs-color-b fw-bold"
                                style={{
                                    lineHeight: 1.3,
                                    wordBreak: 'break-word'
                                }}
                            >
                                {item.title}
                            </small>
                        </div>
                    ))}
                </div>
            </Menu>
            }

            {subtitle &&
                <>
                    <label className="fw-bold label-custom bs-color-b mb-0 text-uppercase">{subtitle}</label>
                    <hr className="hr-muted"/>
                </>
            }
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex flex-column">
                    <label className="fw-bold label-custom bs-color-b mb-2 small">{title}</label>
                    <div className="d-flex flex-column mb-3">
                        <label className="h3 bs-color-b">
                            {formatMoney(totalAmount)}
                        </label>
                    </div>
                </div>

                <div className="text-end">
                    <small className="fw-bold d-block bs-color-b">
                        Hoje
                    </small>

                    <div className="h5 m-0 bs-color-b">
                        {formatMoney(today?.total)}
                    </div>

                    {Number(today?.yesterday_total || 0) > 0 ? (
                        <small
                            style={{
                                color:
                                    today?.trend === 'up'
                                        ? 'var(--bs-chart-green)'
                                        : today?.trend === 'down'
                                            ? '#f87171'
                                            : '#9aa4af',
                                fontWeight: 700
                            }}
                        >
                            {today?.trend === 'up' && '↑ '}
                            {today?.trend === 'down' && '↓ '}
                            {formatPercent(today?.percent_change)} vs ontem
                        </small>
                    ) : (
                        <small style={{ color: '#9aa4af', fontWeight: 700 }}>
                            {Number(today?.total || 0) > 0 ? 'Sem vendas ontem' : 'Sem vendas hoje'}
                        </small>
                    )}
                </div>
            </div>

            <div className="chart-container" style={{ pointerEvents: ready ? 'auto' : 'none' }}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={chartData}
                        margin={{
                            top: 5,
                            right: 10,
                            left: 0,
                            bottom: 5,
                        }}
                        responsive
                    >
                        <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="var(--bs-chart-cartesian)"
                        />

                        <XAxis
                            dataKey="name"
                            stroke="var(--bs-chart-axis)"
                            interval={0}
                            tick={{ fontSize: 12 }}
                            tickLine={false}
                            axisLine={false}
                        />

                        <YAxis
                            stroke="var(--bs-chart-axis)"
                            tickFormatter={(value) => formatNumber(value)}
                            tickLine={false}
                            axisLine={false}
                        />

                        <Tooltip
                            cursor={{ fill: 'var(--bs-chart-fill)' }}
                            content={<CustomTooltip />}
                        />

                        {dataBar.map((item, i) => (
                            <Bar
                                key={item.key}
                                dataKey={item.key}
                                stackId="a"
                                fill={colors[i % colors.length]}
                                name={item.title}
                            />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            </div>

            <div className="mt-3 mb-3">
                <div className={"d-flex justify-content-center gap-2 "+ (presets.length === 5 && 'chart-filter')}>
                    <Button
                        size="small"
                        className={"bs-bg-invert bs-color-b " + (period === '7d' && 'btn-checked')}
                        variant="contained"
                        color="primary"
                        onClick={() => setPeriod('7d')}
                    >
                        7D
                    </Button>

                    <Button
                        size="small"
                        className={"bs-bg-invert bs-color-b " + (period === '1m' && 'btn-checked')}
                        variant="contained"
                        color="primary"
                        onClick={() => setPeriod('1m')}
                    >
                        1M
                    </Button>

                    <Button
                        size="small"
                        className={"bs-bg-invert bs-color-b " + (period === '3m' && 'btn-checked')}
                        variant="contained"
                        color="primary"
                        onClick={() => setPeriod('3m')}
                    >
                        3M
                    </Button>

                    {presets.includes('6m') &&
                        <Button
                            size="small"
                            className={"bs-bg-invert bs-color-b " + (period === '6m' && 'btn-checked')}
                            variant="contained"
                            color="primary"
                            onClick={() => setPeriod('6m')}
                        >
                            6M
                        </Button>
                    }

                    <Button
                        className={"bs-bg-invert bs-color-b " + (period === 'all' && 'btn-checked')}
                        variant="contained"
                        color="primary"
                        onClick={() => setPeriod('all')}
                    >
                        TUDO
                    </Button>

                    <CustomPeriodButton
                        period={period}
                        onApply={({ start_date, end_date }) => {
                            if(start_date && end_date) {
                                setPeriod('personalized')
                            }
                            onChange?.({
                                start_date,
                                end_date
                            })
                        }}
                    />
                </div>
            </div>

            {(legend) &&
            <div className="mt-3 d-flex flex-wrap">
                <div>
                    {legendDisplayData.map(item => (
                        <div
                            key={item.key}
                            className="d-flex align-items-center mb-2"
                            style={{ cursor: item.isOthers ? 'pointer' : 'default' }}
                            onClick={(e) => {
                                if (item.isOthers) {
                                    setOthersData(item.items)
                                    setAnchorEl(e.currentTarget)
                                }
                            }}
                        >
                            <span
                                style={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: 3,
                                    backgroundColor: item.color,
                                    display: 'inline-block',
                                    marginRight: 10,
                                    flexShrink: 0
                                }}
                            />

                            <small
                                className="bs-color-b fw-bold"
                                style={{
                                    lineHeight: 1.3,
                                    wordBreak: 'break-word'
                                }}
                            >
                                {item.title}
                            </small>
                        </div>
                    ))}
                </div>
            </div>
            }
        </>
    )
}